<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex-grow overflow-hidden">
      <aside-content id="catalog" v-model:opened="asideOpened">
        <!-- <template #aside>
          <div class="flex flex-col w-full overflow-hidden">
            <div class="h-16 min-h-[64px] text-center border-b bg-gray-50 flex flex-col items-center">
              <tx-tabs v-model="selectedTab" :tabs="tabs" class="m-auto" />
            </div>
          </div>
        </template> -->
        <!-- Toolbar -->
        <div>
          <assets-toolbar
            :aside-open="asideOpened" :destination="assetsHeader" :is-a-i-assisted="isAiAssisted"
            :select-all="selectAllCheck" :select-all-indeterminate="selectAllIndeterminate"
            :is-any-data-available="listedAssets.length > 0" @select-all-change="onSelectAll" @aside-click="asideOpened = !asideOpened" @filter="filterOpen = true"
            @clear-all-filters="onClearFilter" @clear-filter="onClearFilter" @search="onSearch" @change-account="onChangeAccount" @reverse-lookup="onReverseLookup"
          />
        </div>
        <assets-list
          ref="assetsListRef" :context="duneContext" :context-key="contextKey"
          :thumbnail-size="getThumbnailSize" :search-text="searchText" :filter="filter" :search-mode="searchMode" :image-data="reverseImageData" :allow-selection="true"
          @selection-changed="onSelectionChanged" @data-changed="onAssetsListDataChanged"
          @asset-click="onAssetClick"
        />
        <!-- Action buttons -->
        <action-bar :active="selectedAssets.length > 0" :buttons="actionBarButtons" />
      </aside-content>
    </div>
    <status-bar />
  </div>
  <tx-drawer
    v-model="assetDetailsOpen" width="1024px" right
    @closed="closeAssetDetails"
  >
    <asset-details
      v-if="clickedAsset" :asset="clickedAsset" :context="duneContext"
      :context-key="contextKey" @close="closeAssetDetails"
    />
  </tx-drawer>
  <tx-drawer v-model="filterOpen" width="700px" right>
    <assets-filter
      v-model="filterCriteria" :search-text="searchMode !== 'nlp' ? searchText : ''"
      class="pt-8 px-15" @cancel="filterOpen = false"
      @confirm="onFilterConfirm"
    />
  </tx-drawer>
  <tx-drawer v-model="reverseLookupOpen" width="700px" right>
    <assets-reverse-lookup
      class="pt-8 px-15"
      @cancel="reverseLookupOpen = false"
      @confirm="doReverseLookup"
    />
  </tx-drawer>
  <tx-dialog v-model="searchDialogVisible" show-overlay show-ok-cancel :title="t('general.search')" :confirm-text="t('general.search')" width="500px" @ok="doSearch">
    <div class="flex flex-col w-full h-full p-4">
      <tx-input
        ref="searchInputRef" v-model="tempSearchText" type="text"
        placeholder="Search assets..."
      />
      <div class="mt-4">
        <tx-checkbox v-model="isAiAssisted" :label="t('assetsList.aiAssistedSearch')" />
        <div class="mt-2 text-sm text-gray-600">
          {{ isAiAssisted ? t('assetsList.aiAssistedSearchDescription') : t('assetsList.fullTextSearchDescription') }}
        </div>
      </div>
    </div>
  </tx-dialog>
  <tx-dialog v-model="comparerDialogVisible" show-overlay :title="t('assetsList.compare')" width="95%" height="95%">
    <div class="flex flex-col w-full h-full p-4">
      <assets-comparer ref="assetsComparerRef" :assets="selectedAssets" :context="duneContext" :context-key="contextKey" />
    </div>
  </tx-dialog>
  <seller-account-chooser-dialog ref="refSellerAccountChooserDialog" @account-selected="onAccountSelected" />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import AssetsList from './components/AssetsList.vue'
import AssetsToolbar from './components/AssetsToolbar.vue'
import AssetDetails from './components/AssetDetails.vue'
import AssetsComparer from './components/AssetsComparer.vue'
import AssetsFilter from './components/AssetsFilter.vue'
import AssetsReverseLookup from './components/AssetsReverseLookup.vue'
import { useUserStore } from '@/store/userData'
import type { SearchAssetModel } from '@/api/dune/model/searchAssetModel'
import * as sBar from '@/store/status'
import AsideContent from '@/shared/components/AsideContent.vue'
import StatusBar from '@/shared/components/StatusBar.vue'
// import { useEventListener } from '@/shared/composables/event'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'
import TxDrawer from '@/shared/components/TxDrawer.vue'
import ActionBar from '@/shared/components/ActionBar.vue'
import appConfig from '@/services/appConfig'
import DuneAsset from '@/models/duneAsset'
import { useNotificationStore } from '@/store/notification'
import utils from '@/services/utils'
import SellerAccountChooserDialog from '@/modules/digitalAssets/SellerAccountChooserDialog.vue'

const assetsListRef = ref<InstanceType<typeof AssetsList>>()
const refSellerAccountChooserDialog = ref<InstanceType<typeof SellerAccountChooserDialog>>()

const userStore = useUserStore()
const { t } = useI18n()
const notificationsStore = useNotificationStore()
// useEventListener(window, 'keydown', handleKeyDown)

const asideOpened = ref(false)
const selectedAssets = ref<SearchAssetModel[]>([])
let listedAssets = [] as SearchAssetModel[]
const assetsHeader = ref({ type: 'search', searchText: '*' } as IAssetsToolbarHeader)
const selectAllCheck = ref(false)
const selectAllIndeterminate = ref(false)
const filter = ref('')
const searchMode = ref<'full-text' | 'filter' | 'nlp' | 'reverse-lookup'>('full-text')
const searchText = ref('*')
const isAiAssisted = ref(false)
const searchDialogVisible = ref(false)
const comparerDialogVisible = ref(false)
const tempSearchText = ref('')
const searchInputRef = ref<InstanceType<typeof TxInput>>()
const assetDetailsOpen = ref(false)
const clickedAsset = ref<SearchAssetModel | null>(null)
const assetsComparerRef = ref<InstanceType<typeof AssetsComparer>>()
const filterOpen = ref(false)
const fileNamesForSave = new Map<string, number>()
const reverseLookupOpen = ref(false)
const reverseImageData = ref<string | null>(null)

const filterCriteria = ref<{ prop: string, label: string, type: 'vetting' | 'string' | 'decimal', vetting: string[] | undefined, value: string | string[] | null, rangeValue?: { from: number | null, to: number | null } | null }[]>([
  { prop: 'ColorFamily', label: 'Color Family', type: 'vetting', vetting: ['Blacks', 'Med Indigo - Worn In', 'Dark Indigo - Worn In', 'Light Indigo - Worn In', 'Dark Indigo - Flat Finish', 'Neutrals', 'Light Indigo - Flat Finish', 'Med Indigo - Flat Finish', 'Whites', 'Greens', 'Greys', 'Browns', 'Blues'], value: null },
  { prop: 'MaterialCode', label: 'Material Code', type: 'string', vetting: undefined, value: null },
  { prop: 'PrimaryFabric', label: 'Primary Fabric', type: 'string', vetting: undefined, value: null },
  { prop: 'MaterialType', label: 'Material Type', type: 'vetting', vetting: ['Woven - Denim', 'Woven - Non Denim'], value: null },
  { prop: 'GenericContent', label: 'Generic Content', type: 'string', vetting: undefined, value: null },
  { prop: 'BrandedContent', label: 'Branded Content', type: 'string', vetting: undefined, value: null },
  { prop: 'FabricMill', label: 'Fabric Mill', type: 'string', vetting: undefined, value: null },
  { prop: 'FabricAfterWashWeight', label: 'Fabric After Wash Weight', type: 'string', vetting: undefined, value: null },
  { prop: 'ProductPricePositioning', label: 'Product Price Positioning', type: 'vetting', vetting: ['TIER 2', 'TIER 3'], value: null },
  { prop: 'FabricStretchPerc', label: 'Fabric Stretch %', type: 'string', vetting: undefined, value: null },
  { prop: 'StretchBucket', label: 'Stretch Bucket', type: 'vetting', vetting: ['0% NO STRETCH', '46%-60%', '36%-45%', '21%-35%', '1%-20%', '61%-80%'], value: null },
  { prop: 'Denim', label: 'Denim', type: 'vetting', vetting: ['Denim', 'Non-Denim'], value: null },
  { prop: 'TargetFOB', label: 'Target FOB', type: 'decimal', vetting: undefined, value: null, rangeValue: { from: null, to: null } },
  { prop: 'Performance', label: 'Performance', type: 'vetting', vetting: ['Cool', 'Warm', 'Not Applicable', 'Levi\'s Flex'], value: null },
  { prop: 'Selvedge', label: 'Selvedge', type: 'vetting', vetting: ['No', 'Yes'], value: null },
  { prop: 'StretchType', label: 'Stretch Type', type: 'vetting', vetting: ['Non-Stretch', 'Stretch'], value: null },
  { prop: 'Destruction', label: 'Destruction', type: 'vetting', vetting: ['None', 'Minimal', 'Overt'], value: null },
  { prop: 'ExtendedSizes', label: 'Extended Sizes', type: 'string', vetting: undefined, value: null },
  { prop: 'HemTreatment', label: 'Hem Treatment', type: 'vetting', vetting: ['Finished', 'Clean Hem with Damage', 'Cut Hem with Damage', 'Cut-Off', 'Cut Off BW', 'Rolled'], value: null },
  { prop: 'FabricStory', label: 'Fabric Story', type: 'string', vetting: undefined, value: null },
  { prop: 'FinishType', label: 'Finish Type', type: 'vetting', vetting: ['Worn In', 'Stonewash', 'Destructed', 'Rinse', 'Acid Wash', 'Rigid'], value: null },
  { prop: 'PrimaryFinish', label: 'Primary Finish', type: 'string', vetting: undefined, value: null },
  { prop: 'ShadeColor', label: 'Shade Color', type: 'vetting', vetting: ['Dark', 'Medium', 'Light', 'S Light', 'Saturated'], value: null },
])

const duneContext = computed(() => {
  return userStore.activeCatalog?.DuneContext ?? userStore.sellerDetails?.DuneContext ?? ''
})
const contextKey = computed(() => {
  return userStore.activeCatalog?.ContextKey ?? userStore.sellerDetails?.ContextKey ?? ''
})
const actionBarButtons = computed(() => {
  return [
    {
      id: 'deselect',
      icon: 'fa-light fa-rectangle-xmark',
      label: 'catalog.actionBar.deselect',
      disabled: false,
      onClick: () => { onSelectAll(false) },
    },
    {
      id: 'download',
      label: 'assetsList.download',
      icon: 'fa-light fa-download',
      disabled: selectedAssets.value.length === 0,
      onClick: onSaveImage,
    },
    {
      id: 'compare',
      label: 'assetsList.compare',
      icon: 'fa-light fa-edit',
      disabled: selectedAssets.value.length <= 1,
      onClick: () => {
        comparerDialogVisible.value = true
        assetsComparerRef.value?.init()
      },
    },
  ]
})

const getThumbnailSize = computed(() => userStore.thumbnailSize)

function onSelectionChanged(arts: SearchAssetModel[]) {
  selectedAssets.value = arts
  sBar.setItemValue('selectedAssetsCount', arts.length.toLocaleString())
}

async function onAssetsListDataChanged(assets: SearchAssetModel[]) {
  listedAssets = assets
  sBar.setItemValue('listedAssets', listedAssets.length.toLocaleString())
}

function onAssetClick(asset: SearchAssetModel) {
  clickedAsset.value = asset
  assetDetailsOpen.value = true
}

function getFileNameToSave(asset: DuneAsset) {
  let fileName = asset.FileName
  if ((userStore.activeCatalog && userStore.activeCatalog.Config.SaveDigitalAssetFileName) || (userStore.sellerConfig && userStore.sellerConfig.DownloadArticleImageFormat)) {
    let configuredFileName = ''
    if (userStore.activeCatalog && userStore.activeCatalog.Config.SaveDigitalAssetFileName) {
      configuredFileName = userStore.activeCatalog.Config.SaveDigitalAssetFileName
    }
    else if (userStore.sellerConfig && userStore.sellerConfig.SaveDigitalAssetFileName) {
      configuredFileName = userStore.sellerConfig.SaveDigitalAssetFileName
    }
    const evalFunction = `function(data){ return ${configuredFileName}}`
    // eslint-disable-next-line no-new-func
    const tempFileName = Function(`'use strict';return (${evalFunction})`)()(asset)
    const count = fileNamesForSave.get(fileName) || 0
    fileNamesForSave.set(fileName, count + 1)
    fileName = count !== 0 ? `${tempFileName}(${count})` : tempFileName
  }
  else {
    const name = fileName.split('.').slice(0, -1).join('.')
    fileName = `${name}(${asset.Key})`
  }
  return fileName
}

async function onSaveImage() {
  let format = 'png'
  if (userStore.activeCatalog && userStore.activeCatalog.Config.DownloadArticleImageFormat) {
    format = userStore.activeCatalog.Config.DownloadArticleImageFormat
  }
  else if (userStore.sellerConfig && userStore.sellerConfig.DownloadArticleImageFormat) {
    format = userStore.sellerConfig.DownloadArticleImageFormat
  }

  fileNamesForSave.clear()
  const promises: Promise<any>[] = []
  let directoryHandle: FileSystemDirectoryHandle | null = null

  // Ask for directory picker first
  if (window && typeof window.showDirectoryPicker !== 'undefined') {
    try {
      directoryHandle = await window.showDirectoryPicker({ mode: 'readwrite' })
    }
    catch (error: any) {
      console.error('Error opening directory:', error)
    }
  }

  selectedAssets.value.forEach((asset) => {
    const promise = axios({
      url: `${appConfig.AssetsUrl}/assets/content/${asset.document.StorageFile.toUpperCase()}?ContextKey=${encodeURIComponent(contextKey.value)}&f=${format}`,
      method: 'GET',
      responseType: 'blob',
    }).then((response) => {
      const duneAsset = new DuneAsset(duneContext.value, asset.document)
      const fileName = `${getFileNameToSave(duneAsset)}.${format}`

      saveFile(response.data, fileName, directoryHandle)
    })

    promises.push(promise)
  })

  Promise.all(promises)
    .then(() => {
      notificationsStore.addNotification({ message: t('notifications.digitalAssetsDownloaded'), type: 'Success' })
    })
    .catch((e) => {
      console.error(e)
      notificationsStore.addNotification({ message: t('notifications.digitalAssetsDownloadError'), type: 'Alert', details: utils.getErrorMessage(e), actions: ['ShowDetails'] })
    })
}

// Save file to directory or fallback to browser download
async function saveFile(blob: Blob, fileName: string, directoryHandle: FileSystemDirectoryHandle | null) {
  if (directoryHandle) {
    try {
      const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true }) as FileSystemFileHandle
      const writable = await (fileHandle as any).createWritable()
      await writable.write(blob)
      await writable.close()
    }
    catch (error) {
      console.error('Directory Write Error:', error)
    }
  }
  else { // fall back to default download
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

onMounted(() => {
  sBar.setItems([
    { id: 'listedAssets', label: t('status.listedAssets'), value: '0', icon: '' },
    { id: 'selectedAssetsCount', label: t('status.selectedAssets'), value: '0', icon: '' },
  ])
})

function onSelectAll(val: boolean) {
  if (val) {
    assetsListRef.value?.selectAll()
  }
  else {
    assetsListRef.value?.deselectAll()
  }
}

function onFilterConfirm(criteria: string, criteriaForDisplay: string[], includeSearch: boolean) {
  console.log('Filter applied:', criteria, criteriaForDisplay)
  reverseImageData.value = null
  searchText.value = includeSearch ? searchText.value : '*'
  filter.value = criteria
  filterOpen.value = false
  searchMode.value = 'filter'
  assetsHeader.value = {
    type: 'filter',
    filters: criteriaForDisplay,
  }
}

function onClearFilter() {
  console.log('Filter cleared:', filter)
  searchMode.value = 'full-text'
  searchText.value = '*'
  assetsHeader.value = {
    type: 'search',
    searchText: '*',
  }
  filter.value = ''
}

function onSearch() {
  console.log('Search triggered')
  tempSearchText.value = searchText.value
  searchDialogVisible.value = true
  setTimeout(() => {
    searchInputRef.value?.focus()
  }, 500)
}

function doSearch() {
  if (tempSearchText.value.trim().length > 0) {
    reverseImageData.value = null
    if (isAiAssisted.value) {
      searchMode.value = 'nlp'
    }
    else {
      searchMode.value = 'full-text'
    }
    searchText.value = tempSearchText.value.trim()
  }
  searchDialogVisible.value = false
  assetsHeader.value = {
    type: 'search',
    searchText: searchText.value,
  }
  filter.value = ''
}

function onReverseLookup() {
  reverseLookupOpen.value = true
}

function doReverseLookup(image: string) {
  reverseLookupOpen.value = false
  reverseImageData.value = image
  searchMode.value = 'reverse-lookup'
  searchText.value = ''
}

function closeAssetDetails() {
  assetDetailsOpen.value = false
  clickedAsset.value = null
}

function onChangeAccount() {
  refSellerAccountChooserDialog.value?.showDialog(userStore.sellerDetails?.AccountId)
}

async function onAccountSelected() {
  assetsListRef.value?.reloadAssets()
}

// function handleKeyDown(event: KeyboardEvent) {
//   if ((event.key === 'F' || event.key === 'f') && (event.ctrlKey || event.metaKey)) {
//     onSearch()
//     event.preventDefault()
//   }
// }
</script>

<style lang="scss" scoped>
#catalog {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
